import React from "react";
import { Card } from "@vitalsource/vstui";
import styled from "styled-components";
import { cloud } from "@vitalsource/vstui/colors";
import Dispatcher from "../../../dispatcher";
import Portal from "../../../ui/components/portal";
import OverleafStore, { TYPE_IMAGE } from "../../../stores/overleaf-store";
import BookStore from "../../../stores/book-store";

const BUTTON_MENU_OFFSET = 10;
const MENU_ITEM_INSET = 16;
const MIN_MENU_MARGIN = 10;

const CLOSE_ACTIONS = [
  Dispatcher.BOOK_CLICK,
  Dispatcher.BOOK_HIGHLIGHT_CLICK,
  Dispatcher.BOOK_NOTEICON_CLICK,
  Dispatcher.BOOK_NEXT_PAGE,
  Dispatcher.BOOK_PREVIOUS_PAGE,
  Dispatcher.BOOK_NAVIGATION_REQUESTED,
  Dispatcher.BOOK_PAGE_SCROLL_FULLSPEED,
  Dispatcher.MODAL_OPEN,
  Dispatcher.BOOK_HIGHLIGHT_CLICK,
  Dispatcher.BOOK_NOTEICON_CLICK,
  Dispatcher.BOOK_SELECTION_MADE,
  // Just close on resize since the icon may have moved off-screen, making things weird
  Dispatcher.BOOK_PLACEHOLDER_RESIZE
];

const UPDATE_ACTIONS = [
  Dispatcher.OVERLEAF_DECK_UPDATED,
  Dispatcher.OVERLEAF_DECKS_UPDATED
];

const Styled = {
  OuterContainer: styled.div`
    position: absolute;
    // Fix layering in IE11
    z-index: 2;
  `,
  DeckList: styled.div`
    width: 300px;
    max-width: calc(100vw - ${MIN_MENU_MARGIN * 2}px);
  `,
  SimpleMessage: styled.p`
    margin: 0;
    padding: 12px 19px;
    width: 230px;
    line-height: 19px;
  `,
  ListHeader: styled.h3`
    background: ${cloud.getShade(2)};
    width: 100%;
    font-weight: 300;
    text-transform: uppercase;
    padding: 4px ${MENU_ITEM_INSET}px;
    border-bottom: 1px solid ${cloud.getShade(3)};
  `,
  List: styled.ul`
    list-style-type: none;
    // vstui popover forces 60vh and scrolls the whole thing, not the internals of the lsit
    max-height: 40vh;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0;
    padding: 6px 0;
  `,
  ListItem: styled.li`
    margin: 0;
    padding: 0;
  `,
  ListButton: styled.button`
    border: 2px solid transparent;
    box-shadow: none;
    width: 100%;
    text-align: left;
    padding: 12px ${MENU_ITEM_INSET}px;
    background-color: white;

    &:hover {
      background-color: ${props => props.theme.vstui.color.primary.getShade(1)};
      border-color: ${props => props.theme.vstui.color.primary.getShade(1)};
    }

    &:focus {
      border-color: ${props => props.theme.vstui.color.primary.getShade(6)};
      outline: none;
    }
    &::-moz-focus-inner {
      border: 0;
    }
  `
};

export default class ImagePopover extends React.Component {
  state = {
    isOpen: false,
    decks: null,
    flashcardsError: false,
    imageData: null,
    placementArea: {
      width: 0,
      height: 0
    },
    popoverSize: {
      width: 0,
      height: 0
    },
    windowSize: {
      width: 0,
      height: 0
    },
    figureMap: {}
  };

  componentDidMount() {
    Dispatcher.bind(Dispatcher.BOOK_IMAGE_BUTTON_CLICK, this.onImageClick);
    Dispatcher.bind(Dispatcher.BOOK_FIGURES_LOADED, this.getFigures);
    UPDATE_ACTIONS.forEach(a => Dispatcher.bind(a, this.decksUpdated));
    CLOSE_ACTIONS.forEach(a => Dispatcher.bind(a, this.close));
    this.getFigures(); // call this in case figures load when not mounted
    this.refreshDecks();
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    if (this.state.isOpen && !prevState.isOpen) {
      this.container.focus();
      this.setState({
        popoverSize: {
          width: this.container.offsetWidth,
          height: this.container.offsetHeight
        }
      });
    }
    if (!this.state.isOpen && prevState.isOpen) {
      BookStore.get(this.props.isbn).viewer.Book.fireCallback(
        this.state.imageData.doneCallbackId
      );
    }
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.BOOK_IMAGE_BUTTON_CLICK, this.onImageClick);
    Dispatcher.unbind(Dispatcher.BOOK_FIGURES_LOADED, this.getFigures);
    UPDATE_ACTIONS.forEach(a => Dispatcher.unbind(a, this.decksUpdated));
    CLOSE_ACTIONS.forEach(a => Dispatcher.unbind(a, this.close));
  }

  getFigures = () => {
    const book = BookStore.get(this.props.isbn);
    const figures = book.getFigures();
    if (figures && figures.length > 0) {
      const figureMap = {};
      figures.forEach(fig => {
        figureMap[fig.imageURL] = fig;
      });
      this.setState({ figureMap });
    }
  };

  getPlacementArea() {
    const el = document.querySelector("#jigsaw-placeholder-inner iframe");
    const rect = el.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      top: rect.top,
      left: rect.left
    };
  }

  refreshDecks() {
    OverleafStore.getDecks({ isbn: this.props.isbn })
      .then(decks => {
        this.setState({
          decks
        });
      })
      .catch(() => {
        this.setState({
          flashcardsError: true
        });
      });
  }

  close = () => {
    if (this.state.isOpen) {
      this.setState({
        isOpen: false
      });
    }
  };

  onImageClick = (isbn, e, imageData) => {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
      imageData,
      placementArea: this.getPlacementArea(),
      windowSize: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }));
  };

  decksUpdated = () => {
    this.refreshDecks();
  };

  addToDeck = deck => {
    const url = this.state.imageData.imageURL;
    const figData = this.state.figureMap[url];
    const requestData = {
      deck,
      newCardFront: {
        content: url,
        type: TYPE_IMAGE,
        metadata: {
          caption: figData ? figData.caption : null,
          title: figData ? figData.title : null,
          alt: this.state.imageData.alt
        }
      }
    };
    Dispatcher.emit(Dispatcher.SHOW_UNSEEN_PANEL, "flashcards", requestData);
    Dispatcher.emit(Dispatcher.FLASHCARD_CREATE_REQUEST, requestData);
    this.close();
  };

  /**
   * Nudge on screen if off screen
   * @param windowSize window width or height
   * @param popoverSize popover width or height
   * @param currentPosition popover left or top
   * @return {number}
   */
  positionInWindow(windowSize, popoverSize, currentPosition) {
    if (currentPosition < 0) {
      return MIN_MENU_MARGIN;
    } else if (currentPosition + popoverSize > windowSize) {
      return windowSize - popoverSize - MIN_MENU_MARGIN;
    }
    return currentPosition;
  }

  render() {
    if (!this.state.isOpen) {
      return null;
    }

    // Default: Under button, left edge aligned
    const style = {
      top: this.state.imageData.location.bottom + BUTTON_MENU_OFFSET,
      left: this.state.imageData.location.left
    };

    // Overflow right: align with right edge of button
    if (
      style.left + this.state.popoverSize.width >
      this.state.placementArea.width
    ) {
      style.left =
        this.state.imageData.location.right - this.state.popoverSize.width;
    }

    // Overflow bottom: place above button
    if (
      style.top + this.state.popoverSize.height >
      this.state.placementArea.height
    ) {
      style.top =
        this.state.imageData.location.top -
        BUTTON_MENU_OFFSET -
        this.state.popoverSize.height;
    }

    // Now convert to global context for portal render
    style.left = style.left + this.state.placementArea.left;
    style.top = style.top + this.state.placementArea.top;

    style.left = this.positionInWindow(
      this.state.windowSize.width,
      this.state.popoverSize.width,
      style.left
    );
    style.top = this.positionInWindow(
      this.state.windowSize.height,
      this.state.popoverSize.height,
      style.top
    );

    return (
      <Portal>
        <Styled.OuterContainer
          style={style}
          innerRef={el => (this.container = el)}
          tabIndex="-1"
        >
          <Card raised>
            {this.state.flashcardsError ? (
              <Styled.SimpleMessage>
                {i18n.t("flashcards.images.deck-error")}
              </Styled.SimpleMessage>
            ) : !this.state.decks ? (
              <Styled.SimpleMessage>
                {i18n.t("base.loading")}
              </Styled.SimpleMessage>
            ) : this.state.decks.length === 0 ? (
              <Styled.SimpleMessage>
                {i18n.t("flashcards.images.no-decks")}
              </Styled.SimpleMessage>
            ) : (
              <Styled.DeckList>
                <Styled.ListHeader>
                  {i18n.t("reader.flashcards.create-card")}
                </Styled.ListHeader>
                <Styled.List>
                  {this.state.decks.map(d => (
                    <Styled.ListItem key={d.uuid}>
                      <Styled.ListButton
                        type="button"
                        onClick={() => this.addToDeck(d)}
                      >
                        {d.title}
                      </Styled.ListButton>
                    </Styled.ListItem>
                  ))}
                </Styled.List>
              </Styled.DeckList>
            )}
          </Card>
        </Styled.OuterContainer>
      </Portal>
    );
  }
}
